import { red } from '@mui/material/colors';
import React, { useContext } from 'react';
import { get_cod } from '../../../utils/cod';
import {
  displayLocation,
  extractMapsPlaceLink,
} from '../../../utils/locations';
import { add, displayMoney } from '../../../utils/money';

import CopyLinkButton from '../../common/copy-link-button/copy-link-button';

import { GenericList } from '../../common/generic-list/generic-list';
import {
  IconWithText,
  mobileDoubleItemsGrid,
} from '../../common/styles/components';
import { showDeliveryBatchChip } from '../../common/styles/delivery-batch.styles';
import { showDeliveryState } from '../../common/styles/order-states.style';
import {
  getPickupTaskStateColor,
  getPickupTaskStateIcon,
  showPickupTaskChip,
  showPickupTaskState,
} from '../../common/styles/pickup-task.styles';
import { showWorkflow } from '../../common/styles/workflow-display';
import { Updater } from 'use-immer';
import { SortingByColumnState } from '../../common/generic-table/utils';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LinkIcon from '@mui/icons-material/Link';
import { IPickupTask } from '../../../interfaces/delivery-company/tasks';
import { format } from 'date-fns';
import { Box, Chip, CircularProgress, IconButton } from '@mui/material';
import { AuthContext } from '../../../contexts/user-context/user-context';
import HouseIcon from '@mui/icons-material/House';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DownloadIcon from '@mui/icons-material/Download';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import CancelIcon from '@mui/icons-material/Cancel';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { DriverWithIcon } from '../../common/styles/driver-with-icon';
import { FactCheckOutlined } from '@mui/icons-material';
import {
  DriverInvoiceState,
  DriverInvoiceType,
  IDriverInvoice,
} from '../../../interfaces/common/driver-invoices';
import { showDriverInvoiceState } from '../../../utils/driver-invoices';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import { ClearIcon } from '@mui/x-date-pickers';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment';
import NotesIcon from '@mui/icons-material/Notes';
import {
  getDriverInvoiceStateColor,
  getDriverInvoiceStateIcon,
  showDriverInvoiceType,
} from '../../common/styles/driver-invoice';
import { DataResources, ResourceActionType, user_has_data_model_access } from '../../../utils/permissions';

interface ResponsiveDriverInvoicesListingProps {
  invoicesList: IDriverInvoice[];
  isLoading: boolean;
  target: 'driver' | 'merchant';
  setLockedInvoiceMeta: React.Dispatch<
    React.SetStateAction<IDriverInvoice | null>
  >;
  setLockType: React.Dispatch<React.SetStateAction<'view' | 'delete' | null>>;
  setConfirmUndoInvoice: React.Dispatch<
    React.SetStateAction<IDriverInvoice | null>
  >;
  setConfirmPaymentFor: React.Dispatch<
    React.SetStateAction<IDriverInvoice | null>
  >;
  handleEditNote: (invoice_pk: string) => Promise<void>;
  legacyFetchInvoiceContent: (invoicemeta: IDriverInvoice) => Promise<void>;
  fetchInvoiceContent: (invoicemeta: IDriverInvoice) => Promise<void>;
  lockedInvoiceMeta: IDriverInvoice | null;
  lockType: 'view' | 'delete' | null;
}

const ResponsiveInvoicesListing = ({
  lockType,
  lockedInvoiceMeta,
  setConfirmUndoInvoice,
  fetchInvoiceContent,
  legacyFetchInvoiceContent,
  handleEditNote,
  invoicesList,
  isLoading,
  target,
  setLockedInvoiceMeta,
  setLockType,
  setConfirmPaymentFor,
}: ResponsiveDriverInvoicesListingProps) => {
  const { user, config } = useContext(AuthContext);

  const canView = target == 'driver' ? user_has_data_model_access(user, DataResources.DRIVER_INVOICE, ResourceActionType.VIEW) : user_has_data_model_access(user, DataResources.MERCHANT_INVOICE, ResourceActionType.VIEW) 
  const canEdit = target == 'driver' ? user_has_data_model_access(user, DataResources.DRIVER_INVOICE, ResourceActionType.UPDATE) : user_has_data_model_access(user, DataResources.MERCHANT_INVOICE, ResourceActionType.UPDATE)
  const canUndo = target == 'driver' ?   user_has_data_model_access(user, DataResources.DRIVER_INVOICE, ResourceActionType.DELETE) : user_has_data_model_access(user, DataResources.MERCHANT_INVOICE, ResourceActionType.DELETE)
  
  return (
    <GenericList<IDriverInvoice>
      sourceView="driver-invoices-listing"
      items={invoicesList}
      selectedItems={new Map()}
      updateSelectedItems={() => {
        throw new Error('unimplemented');
      }}
      isLoading={isLoading}
      fullWidth
      columns={[
        {
          id: 'id',
          header: <b>Invoice #</b>,
          display_body: (invoice) => <b>{invoice.id}</b>,
        },
        {
          id: 'for',
          header: <b>For</b>,
          display_body: (invoice) => <DriverWithIcon driver={invoice.driver} />,
        },
        {
          id: 'purpose',
          header: <b>Type</b>,
          display_body: (invoice) => {
            const purposeLabels = {
              'NET': 'Net Settlement',
              'COLLECTION': 'Collection',
              'COMMISSION': 'Commission'
            };
            const purposeColors = {
              'NET': 'primary',
              'COLLECTION': 'success',
              'COMMISSION': 'info'
            } as const;
            return (
              <Chip
                label={purposeLabels[invoice.purpose] || invoice.purpose}
                size="small"
                color={purposeColors[invoice.purpose] || 'default'}
                variant="outlined"
              />
            );
          },
        },
        {
          id: 'commission',
          header: <b>Commission</b>,
          display_body: (invoice) =>
            displayMoney(
              add(
                invoice.total_amounts.driver_commission,
                invoice.total_amounts.pop
              )
            ),
        },
        {
          id: 'due_amount',
          header: <b>Due Amount</b>,
          display_body: (invoice) => (
            <>{displayMoney(invoice.total_amounts.due_to_company)}</>
          ),
        },
        {
          id: 'created_at',
          header: <b>Created At</b>,
          display_body: (invoice) => (
            <>{new Date(invoice.created_at).toLocaleString()}</>
          ),
        },
        {
          id: 'payment_state',
          header: <b>Payment State</b>,
          display_body: (invoice) => {
            return (
              <>
                {invoice.status == DriverInvoiceState.PENDING_PAYMENT ? (
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => setConfirmPaymentFor(invoice)}
                  >
                    {showDriverInvoiceState(invoice.status)}
                  </div>
                ) : (
                  showDriverInvoiceState(invoice.status)
                )}
              </>
            );
          },
        },
        {
          id: 'notes',
          header: <b>Notes</b>,
          display_body: (invoice) => (
            <>
              {' '}
              {invoice.notes}
              <IconButton
                onClick={() => {
                  handleEditNote(invoice.id);
                }}
                disabled={!canEdit}
                size='small'
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </>
          ),
        },
        {
          id: 'download_invoice',
          header: <b>Download</b>,
          display_body: (invoice) => (
            <>
              {' '}
              <IconButton
                onClick={() => fetchInvoiceContent(invoice)}
                disabled={
                  lockedInvoiceMeta !== null || !canView
                }
              >
                {lockedInvoiceMeta?.id === invoice.id && lockType === 'view' ? (
                  <CircularProgress size={20} />
                ) : (
                  <DownloadIcon fontSize="small" />
                )}
              </IconButton>
            </>
          ),
        },
        {
          id: 'undo_invoice',
          header: <b>Undo Invoice</b>,
          display_body: (invoice) => (
            <>
              <IconButton
                size="small"
                style={{ color: 'red' }}
                disabled={lockedInvoiceMeta !== null || !canUndo}
                onClick={() => {
                  setLockType('delete');
                  setLockedInvoiceMeta(invoice);
                  setConfirmUndoInvoice(invoice);
                }}
              >
                {lockedInvoiceMeta?.id === invoice.id &&
                lockType === 'delete' ? (
                  <CircularProgress size={20} />
                ) : (
                  <RestorePageIcon fontSize="small" style={{ color: 'red' }} />
                )}
              </IconButton>
            </>
          ),
        },
      ]}
      getItemUID={(order) => order.id}
      rowConfigs={[
        {
          display_row: (invoice) => <b>Invoice #{invoice.id}</b>,
        },
        {
          display_row: (invoice) => (
            <DriverWithIcon centered driver={invoice.driver} />
          ),
        },
        {
          display_row: (invoice) => (
            <IconWithText centered>
              {new Date(invoice.created_at).toLocaleString()}
            </IconWithText>
          ),
        },
        {
          display_row: (invoice) =>
            mobileDoubleItemsGrid([
              showDriverInvoiceType(invoice.type),
              <Box
                key={'items'}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '12px',
                  padding: '4px 8px',
                }}
              >
                {invoice.type === DriverInvoiceType.PICKUP
                  ? `${invoice.task_count} pickups`
                  : `${invoice.packages} packages`}
              </Box>,
            ]),
        },
        {
          display_row: (invoice) =>
            mobileDoubleItemsGrid([
              <div
                key={'amount'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>{'Total Commission'}</b>{' '}
                {displayMoney(invoice.total_amounts.driver_commission)}
              </div>,
              <div
                key={'amount'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>{'Total Due'}</b>{' '}
                {displayMoney(invoice.total_amounts.due_to_company)}
              </div>,
            ]),
        },
        {
          display_row: (invoice) =>
            mobileDoubleItemsGrid([
              <div
                key={'invoice notes'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>Notes</b>
                <>{invoice.notes}</>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditNote(invoice.id);
                  }}
                  disabled={!canEdit}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </div>,
              invoice.status === DriverInvoiceState.PENDING_PAYMENT && (
                <div
                  key={'undo invoice'}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <b>Undo</b>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setLockType('delete');
                      setLockedInvoiceMeta(invoice);
                      setConfirmUndoInvoice(invoice);
                    }}
                    disabled={lockedInvoiceMeta !== null || !canUndo}
                  >
                    {lockedInvoiceMeta?.id === invoice.id &&
                    lockType === 'delete' ? (
                      <CircularProgress size={20} />
                    ) : (
                      <RestorePageIcon
                        fontSize="small"
                        style={{ color: 'red' }}
                      />
                    )}
                  </IconButton>
                </div>
              ),
            ]),
        },
      ]}
      cardFooter={(invoice) => {
        return {
          content: (
            <IconWithText centered>
              {getDriverInvoiceStateIcon(invoice.status)}
              {invoice.status == DriverInvoiceState.PENDING_PAYMENT ? (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmPaymentFor(invoice);
                  }}
                >
                  {invoice.status.replaceAll('_', ' ')}
                </div>
              ) : (
                invoice.status.replaceAll('_', ' ')
              )}
            </IconWithText>
          ),
          background: getDriverInvoiceStateColor(invoice.status),
        };
      }}
      cardIcon={(invoice) => <MonetizationOnIcon color="primary" />}
      onCardClick={(invoice) => canView && fetchInvoiceContent(invoice)}
    />
  );
};

export default ResponsiveInvoicesListing;
